import { operators as o, common as c } from '@pendo/aggregations';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { applyCustomSegment } from './common.util';

const getFirstTimeInAppExpression = (metadataAppPath: string) => {
    return `if(isNil(metadata.auto${metadataAppPath}.firstidentifiedvisit), metadata.auto${metadataAppPath}.firstvisit, metadata.auto${metadataAppPath}.firstidentifiedvisit)`;
};

const getVisitorFirstTimeInAppExpression = (metadataAppPath: string) => {
    return `if(isNil(visitor.auto${metadataAppPath}.firstidentifiedvisit), visitor.auto${metadataAppPath}.firstvisit, visitor.auto${metadataAppPath}.firstidentifiedvisit)`;
};

export default {
    name: 'PercentageOnboardedAggregation',
    productArea: PRODUCT_AREAS.IMPACT_BRIEF,
    subProductArea: SUB_PRODUCT_AREAS.ONBOARDING,
    responseFormat: 'row',
    build: ({
        appId,
        targetType,
        targetId,
        firstComparisonRange,
        secondComparisonRange,
        thirdComparisonRange,
        expectedDaysToOnboard,
        usesAccounts,
        segmentId,
    }: {
        appId: c.AppIdType;
        targetType: c.TargetType;
        targetId: c.TrackTypeId;
        firstComparisonRange: string;
        secondComparisonRange: string;
        thirdComparisonRange: string;
        expectedDaysToOnboard: number;
        usesAccounts?: boolean;
        segmentId?: string;
    }) => {
        if (!appId) {
            throw new Error('appId is required for PercentageOnboardedAggregation');
        }
        if (!firstComparisonRange || !secondComparisonRange) {
            throw new Error('comparisonRange is required for PercentageOnboardedAggregation');
        }
        const metadataAppPath = `_${appId.toString().replace('-', '_')}`;

        return o.pipeline(
            usesAccounts
                ? accountsSpawnPipeline(
                    appId,
                    targetType,
                    targetId,
                    firstComparisonRange,
                    secondComparisonRange,
                    expectedDaysToOnboard,
                    metadataAppPath,
                    segmentId,
                )
                : visitorsSpawnPipeline(
                    appId,
                    targetType,
                    targetId,
                    firstComparisonRange,
                    secondComparisonRange,
                    expectedDaysToOnboard,
                    metadataAppPath,
                    segmentId,
                ),
            o.join('id'),
            o.evaluate('percentBefore', 'completedCountBefore/newCountBefore * 100'),
            o.evaluate('percentAfter', 'completedCountAfter/newCountAfter * 100'),
            o.select({
                percentBefore: 'percentBefore',
                percentAfter: 'percentAfter',
                id: 'id',
                completedCountBefore: 'completedCountBefore',
                completedCountAfter: 'completedCountAfter',
                newCountBefore: 'newCountBefore',
                newCountAfter: 'newCountAfter',
                startFirstPeriod: `formatTime("Jan 2, 2006", ${firstComparisonRange})`,
                endFirstPeriod: `formatTime("Jan 2, 2006", ${thirdComparisonRange})`,
                startSecondPeriod: `formatTime("Jan 2, 2006", ${secondComparisonRange})`,
                endSecondPeriod: `formatTime("Jan 2, 2006", dateAdd(startOfPeriod("daily", ${firstComparisonRange}),-1, "days"))`,
            }),
        );
    },
};

function visitorsSpawnPipeline (
    appId: c.AppIdType,
    targetType: c.TargetType,
    targetId: c.TrackTypeId,
    firstComparisonRange: string,
    secondComparisonRange: string,
    expectedDaysToOnboard: number,
    metadataAppPath: string,
    segmentId?: string,
): c.PipelineSpawn | c.InvalidOperator {
    return o.spawn(
        completeVisitorsPipeline(
            appId,
            targetType,
            targetId,
            metadataAppPath,
            expectedDaysToOnboard,
            'completedCountAfter',
            o.filter(`timeOfEventCompletion >= ${firstComparisonRange}`),
            o.filter(`firstTimeInApp >= ${firstComparisonRange}`),
            segmentId,
        ),
        newVisitorsPipeline(
            appId,
            targetId,
            metadataAppPath,
            'newCountAfter',
            o.filter(`firstTimeInApp >= ${firstComparisonRange}`),
            segmentId,
        ),
        completeVisitorsPipeline(
            appId,
            targetType,
            targetId,
            metadataAppPath,
            expectedDaysToOnboard,
            'completedCountBefore',
            o.filter(
                `timeOfEventCompletion >= ${secondComparisonRange} && timeOfEventCompletion < ${firstComparisonRange}`,
            ),
            o.filter(`firstTimeInApp >= ${secondComparisonRange} && firstTimeInApp < ${firstComparisonRange}`),
            segmentId,
        ),
        newVisitorsPipeline(
            appId,
            targetId,
            metadataAppPath,
            'newCountBefore',
            o.filter(`firstTimeInApp >= ${secondComparisonRange} && firstTimeInApp <= ${firstComparisonRange}`),
            segmentId,
        ),
    );
}

function completeVisitorsPipeline (
    appId: c.AppIdType,
    targetType: c.TargetType,
    targetId: c.TrackTypeId,
    metadataAppPath: string,
    expectedDaysToOnboard: number,
    reduceKey: string,
    eventComparisonFilter: c.PipelineFilter | c.InvalidOperator,
    appComparisonFilter: c.PipelineFilter | c.InvalidOperator,
    segmentId?: string,
): c.PipelineData[] {
    return o.pipeline(
        o.sources.timespan({ targetType, targetId }),
        o.identified('visitorId'),
        segmentId && !['everyoneExcludingAnonymous', 'everyone'].includes(segmentId) ? o.segment(segmentId) : null,
        o.bulkExpand('visitor'),
        o.select({
            visitorId: 'visitorId',
            timeOfEventCompletion: getVisitorFirstTimeInAppExpression(metadataAppPath),
        }),
        eventComparisonFilter,
        o.group('visitorId', o.groupField('timeOfEventCompletion', { min: 'timeOfEventCompletion' })),
        o.merge(
            ['visitorId'],
            o.mappings({
                firstTimeInApp: 'firstTimeInApp',
            }),
            o.pipeline(
                o.sources.visitors({ appId }),
                segmentId && !['everyoneExcludingAnonymous', 'everyone'].includes(segmentId)
                    ? o.segment(segmentId)
                    : null,
                o.select({
                    firstTimeInApp: getFirstTimeInAppExpression(metadataAppPath),
                    visitorId: 'visitorId',
                }),
                appComparisonFilter,
                o.group('visitorId', o.groupField('firstTimeInApp', { min: 'firstTimeInApp' })),
            ),
        ),
        o.filter(
            `firstTimeInApp >= dateAdd(startOfPeriod("daily",timeOfEventCompletion),-${expectedDaysToOnboard}, "days") && firstTimeInApp <= timeOfEventCompletion`,
        ),
        o.reduce({
            [reduceKey]: o.count('visitorId'),
        }),
        o.evaluate('id', `"percentageOnboardedAggregation_${targetId}"`),
    );
}

function newVisitorsPipeline (
    appId: c.AppIdType,
    targetId: c.TrackTypeId,
    metadataAppPath: string,
    reduceKey: string,
    comparisonFilter: c.PipelineFilter | c.InvalidOperator,
    segmentId?: string,
): c.PipelineData[] {
    return o.pipeline(
        o.sources.visitors({ appId }),
        segmentId && !['everyoneExcludingAnonymous', 'everyone'].includes(segmentId) ? o.segment(segmentId) : null,
        o.select({
            firstTimeInApp: getFirstTimeInAppExpression(metadataAppPath),
            visitorId: 'visitorId',
        }),
        comparisonFilter,
        o.reduce({
            [reduceKey]: o.count('visitorId'),
        }),
        o.evaluate('id', `"percentageOnboardedAggregation_${targetId}"`),
    );
}

function accountsSpawnPipeline (
    appId: c.AppIdType,
    targetType: c.TargetType,
    targetId: c.TrackTypeId,
    firstComparisonRange: string,
    secondComparisonRange: string,
    expectedDaysToOnboard: number,
    metadataAppPath: string,
    segmentId?: string,
): c.PipelineSpawn | c.InvalidOperator {
    return o.spawn(
        completeAccountsPipeline(
            appId,
            targetType,
            targetId,
            metadataAppPath,
            expectedDaysToOnboard,
            'completedCountBefore',
            o.filter(`firstTimeInApp >= ${firstComparisonRange}`),
            segmentId,
        ),
        newAccountsPipeline(
            appId,
            targetId,
            metadataAppPath,
            'newCountBefore',
            o.filter(`firstTimeInApp >= ${firstComparisonRange}`),
            segmentId,
        ),
        completeAccountsPipeline(
            appId,
            targetType,
            targetId,
            metadataAppPath,
            expectedDaysToOnboard,
            'completedCountAfter',
            o.filter(`firstTimeInApp >= ${secondComparisonRange} && firstTimeInApp < ${firstComparisonRange}`),
            segmentId,
        ),
        newAccountsPipeline(
            appId,
            targetId,
            metadataAppPath,
            'newCountAfter',
            o.filter(`firstTimeInApp >= ${secondComparisonRange} && firstTimeInApp <= ${firstComparisonRange}`),
            segmentId,
        ),
    );
}

function completeAccountsPipeline (
    appId: c.AppIdType,
    targetType: c.TargetType,
    targetId: c.TrackTypeId,
    metadataAppPath: string,
    expectedDaysToOnboard: number,
    reduceKey: string,
    comparisonFilter: c.PipelineFilter | c.InvalidOperator,
    segmentId?: string,
): c.PipelineData[] {
    return o.pipeline(
        o.sources.timespan({ targetType, targetId }),
        o.identified('visitorId'),
        applyCustomSegment(
            o.pipeline(
                o.sources.accounts({ appId }),
                o.select({
                    firstTimeInApp: `metadata.auto${metadataAppPath}.firstvisit`,
                    accountId: 'accountId',
                }),
                comparisonFilter,
            ),
            segmentId,
        ),
        o.group('accountId', o.groupField('firstTime', { min: 'firstTime' })),
        o.bulkExpand('account', 'account', 'accountId'),
        o.evalExpression({
            firstTimeInApp: `account.auto${metadataAppPath}.firstvisit`,
            timeBeforeConversion: 'dateDist(firstTimeInApp, firstTime, "daily")',
        }),
        o.filter(`timeBeforeConversion >= 0 && timeBeforeConversion <= ${expectedDaysToOnboard}`),
        o.reduce({
            [reduceKey]: o.count('accountId'),
        }),
        o.evaluate('id', `"percentageOnboardedAggregation_${targetId}"`),
    );
}

function newAccountsPipeline (
    appId: c.AppIdType,
    targetId: c.TrackTypeId,
    metadataAppPath: string,
    reduceKey: string,
    comparisonFilter: c.PipelineFilter | c.InvalidOperator,
    segmentId?: string,
): c.PipelineData[] {
    return o.pipeline(
        o.sources.accounts({ appId }),
        segmentId && !['everyoneExcludingAnonymous', 'everyone'].includes(segmentId) ? o.segment(segmentId) : null,
        o.select({
            firstTimeInApp: `metadata.auto${metadataAppPath}.firstvisit`,
            accountId: 'accountId',
        }),
        comparisonFilter,
        o.reduce({
            [reduceKey]: o.count('accountId'),
        }),
        o.evaluate('id', `"percentageOnboardedAggregation_${targetId}"`),
    );
}
